import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import AutoPlayVideo from '@/components/media/AutoPlayVideo';

import { imageTools } from '@/constants/imageTools';

// Posters for the tools whose preview is a video — without one the card is a
// black box until the clip buffers.
const VIDEO_POSTERS: Record<string, string> = {
  'animate-image':
    'https://media.easy-peasy.ai/landing/home/vid-butterfly-poster.jpg',
};

// Marketing-landing overrides for tools whose slug page is not the best
// destination (mirrors ImageGeneratorFeatures' switch).
const LANDING_HREFS: Record<string, string> = {
  'headshot-generator': '/ai-headshot-generator',
  'creative-upscale': '/free-tools/upscale-image',
  'animate-image': '/ai-animate-image',
  'interior-design': '/ai-interior-design-generator',
  'custom-ai-model': '/ai-photo-generator',
};

const FEATURED_SLUGS = [
  'ai-image-editor',
  'remove-background',
  'replace-background',
  'headshot-generator',
  'creative-upscale',
  'character-reference',
  'style-transfer',
  'sketch-to-image',
  'ghiblify',
  'animate-image',
  'restore-photo',
  'custom-ai-model',
];

const ImageToolsWall = () => {
  const { t } = useLocale('image-tools');
  const { t: tp } = useLocale('ai-image-generator');

  const tools = FEATURED_SLUGS.flatMap((slug) => {
    const tool = imageTools.find((candidate) => candidate.slug === slug);
    return tool ? [{ ...tool, slug }] : [];
  });

  return (
    <section className='relative py-20 lg:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {tp('Generate is half the story — edit anything')}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
            {tp(
              'Retouch, restyle, upscale, swap backgrounds or train a model on your own face.',
            )}
          </p>
        </div>

        <div className='grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4'>
          {tools.map((tool) => (
            <Link
              key={tool.slug}
              href={LANDING_HREFS[tool.slug] || `/image-tools/${tool.slug}`}
              className='group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] transition-all hover:border-indigo-400/40 hover:bg-white/[0.07]'
            >
              <div className='relative aspect-[16/10] w-full overflow-hidden bg-black/40'>
                {tool.isVideoPreview ? (
                  <AutoPlayVideo
                    src={tool.image}
                    poster={VIDEO_POSTERS[tool.slug]}
                    className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
                  />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={tool.image}
                    alt={t(tool.title)}
                    loading='lazy'
                    decoding='async'
                    className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
                  />
                )}
              </div>
              <div className='p-4'>
                <h3 className='text-sm font-bold text-white md:text-base'>
                  {t(tool.title)}
                </h3>
                <p className='mt-0.5 hidden text-sm leading-snug text-slate-400 sm:line-clamp-1'>
                  {t(tool.description)}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className='mt-10 text-center'>
          <Link
            href='/image-tools'
            className='rounded-full border border-white/15 bg-white/5 px-8 py-3 font-semibold text-white transition-all hover:border-white/40 hover:bg-white/10'
          >
            {tp('Explore all image tools')} →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ImageToolsWall;
